/**
 * Design tokens. Every color, size and font in the app comes from here.
 * Components read colors through `useTheme()` so light/dark stay in sync.
 */

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

/** Horizontal padding for every screen. */
export const screenGutter = spacing.lg;

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  pill: 999,
} as const;

/** Minimum touch target (Android guideline is 48dp). */
export const minTouchTarget = 48;

export const fontFamily = {
  regular: 'Inter_400Regular',
  medium: 'Inter_500Medium',
  semibold: 'Inter_600SemiBold',
  bold: 'Inter_700Bold',
} as const;

export const typography = {
  /** The one number that matters on a screen. */
  hero: { fontFamily: fontFamily.bold, fontSize: 44, lineHeight: 52, letterSpacing: -1.4 },
  display: { fontFamily: fontFamily.semibold, fontSize: 40, lineHeight: 48, letterSpacing: -0.8 },
  title: { fontFamily: fontFamily.semibold, fontSize: 22, lineHeight: 28, letterSpacing: -0.3 },
  section: { fontFamily: fontFamily.semibold, fontSize: 17, lineHeight: 24 },
  body: { fontFamily: fontFamily.regular, fontSize: 15, lineHeight: 22 },
  bodyStrong: { fontFamily: fontFamily.medium, fontSize: 15, lineHeight: 22 },
  secondary: { fontFamily: fontFamily.regular, fontSize: 13, lineHeight: 18 },
  caption: { fontFamily: fontFamily.medium, fontSize: 12, lineHeight: 16 },
} as const;

export type TypographyVariant = keyof typeof typography;

const light = {
  background: '#F7F7F5',
  surface: '#FFFFFF',
  surfaceMuted: '#EFEFEB',
  border: '#E4E4DF',
  text: '#16181D',
  textSecondary: '#5E636E',
  textTertiary: '#6F747D',
  primary: '#1F6F5C',
  primaryPressed: '#185A4A',
  primarySoft: '#E1F0EA',
  onPrimary: '#FFFFFF',
  danger: '#B42318',
  dangerSoft: '#FDECEA',
  onDanger: '#FFFFFF',
  success: '#1F7A3F',
  successSoft: '#E3F3E8',
  toastBackground: '#16181D',
  toastText: '#F2F3F5',
  toastAction: '#7DD8BC',
};

const dark: typeof light = {
  background: '#0F1012',
  surface: '#17191C',
  surfaceMuted: '#202328',
  border: '#2A2D33',
  text: '#F2F3F5',
  textSecondary: '#A6ABB4',
  textTertiary: '#8A8F98',
  primary: '#4CC2A0',
  primaryPressed: '#3AAA8A',
  primarySoft: '#16332B',
  onPrimary: '#0B1F19',
  danger: '#F2846B',
  dangerSoft: '#3A1E18',
  onDanger: '#1F0B06',
  success: '#5BCB82',
  successSoft: '#163322',
  toastBackground: '#F2F3F5',
  toastText: '#16181D',
  toastAction: '#1F6F5C',
};

export const colors = { light, dark };

export type ColorScheme = keyof typeof colors;
export type ThemeColors = typeof light;
export type ColorToken = keyof ThemeColors;

/**
 * Muted category palette. Categories store the key (e.g. 'amber'), never a hex value.
 * `fg` is used for icons/text, `bg` for the soft circle behind the icon.
 */
export const categoryPalette = {
  amber: { light: { fg: '#A15C07', bg: '#FDF1DC' }, dark: { fg: '#F5B95A', bg: '#3A2A12' } },
  blue: { light: { fg: '#1D5FB8', bg: '#E3EDFB' }, dark: { fg: '#7FB0F5', bg: '#172A45' } },
  violet: { light: { fg: '#6A3FC1', bg: '#EEE7FB' }, dark: { fg: '#B79CF5', bg: '#2A1F45' } },
  rose: { light: { fg: '#B4234F', bg: '#FCE7EE' }, dark: { fg: '#F390AE', bg: '#401A27' } },
  teal: { light: { fg: '#0F766E', bg: '#DDF3F0' }, dark: { fg: '#5ED0C4', bg: '#123733' } },
  green: { light: { fg: '#2F7D32', bg: '#E5F3E5' }, dark: { fg: '#83CF86', bg: '#1B3520' } },
  orange: { light: { fg: '#B54708', bg: '#FDEBDD' }, dark: { fg: '#F7A26B', bg: '#3D2414' } },
  indigo: { light: { fg: '#3B4BC8', bg: '#E6E9FB' }, dark: { fg: '#9AA6F7', bg: '#1E2347' } },
  pink: { light: { fg: '#A8327F', bg: '#FBE6F4' }, dark: { fg: '#EE93CF', bg: '#3D1B32' } },
  cyan: { light: { fg: '#0E6F8F', bg: '#DFF1F7' }, dark: { fg: '#6CC8E6', bg: '#123241' } },
  slate: { light: { fg: '#4B5563', bg: '#ECEDEF' }, dark: { fg: '#B0B6C0', bg: '#262A30' } },
} as const;

export type CategoryColor = keyof typeof categoryPalette;

export const motion = {
  fast: 150,
  normal: 220,
  slow: 300,
  pressScale: 0.97,
} as const;
